import type { ComponentType, ReactNode } from "react";
import { IconInbox } from "./icons";

/**
 * Laadplaceholder (UX-RICHTLIJNEN §8): rustige grijze balken met een zachte glans in plaats van de tekst "Laden…".
 * De tekst blijft voor schermlezers. `page` vult een hele pagina (kop + tegels + regels), anders een paar regels.
 */
export function Loading({
  page = false,
  lines = 3,
  small = false,
}: {
  page?: boolean;
  lines?: number;
  small?: boolean;
}) {
  const n = small ? Math.min(lines, 2) : lines;
  return (
    <div className={`skel${page ? " skel-page" : ""}${small ? " skel-small" : ""}`} role="status" aria-live="polite">
      <span className="sr-only">Laden…</span>
      {page && (
        <>
          <span className="skel-bar skel-label" aria-hidden="true" />
          <span className="skel-bar skel-title" aria-hidden="true" />
          <span className="skel-tiles" aria-hidden="true">
            <span className="skel-bar" />
            <span className="skel-bar" />
            <span className="skel-bar" />
          </span>
        </>
      )}
      {Array.from({ length: n }, (_, i) => (
        <span key={i} className="skel-bar skel-line" aria-hidden="true" />
      ))}
    </div>
  );
}

/**
 * Lege staat (UX-RICHTLIJNEN §8): icoon, korte titel, één regel uitleg en hooguit één actie.
 * Gebruik voor een lege lijst of een zoekopdracht zonder resultaat, niet voor foutmeldingen.
 */
export function EmptyState({
  title,
  text,
  action,
  icon: Icon = IconInbox,
  compact = false,
}: {
  title: ReactNode;
  text?: ReactNode;
  action?: ReactNode;
  icon?: ComponentType<{ size?: number; "aria-hidden"?: boolean | "true" }>;
  compact?: boolean;
}) {
  return (
    <div className={`empty-state${compact ? " compact" : ""}`}>
      <span className="empty-ico" aria-hidden="true">
        <Icon size={compact ? 20 : 24} aria-hidden="true" />
      </span>
      <p className="empty-title">{title}</p>
      {text && <p className="empty-text">{text}</p>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}
